import type { DeviceKind, DeviceHealth, MediaDeviceConfig } from "../types";

const BALANCED_VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  width: { ideal: 640 },
  height: { ideal: 360 },
  frameRate: { ideal: 24, max: 30 },
};

const mergeTrackConstraints = (
  constraint: MediaTrackConstraints | boolean | undefined,
  defaults: MediaTrackConstraints,
  deviceId: string | null,
): MediaTrackConstraints | boolean => {
  if (constraint === false) return false;
  const merged = {
    ...defaults,
    ...(typeof constraint === "object" ? constraint : {}),
  } as MediaTrackConstraints;

  if (deviceId && merged.deviceId === undefined) {
    merged.deviceId = { exact: deviceId };
  }

  return merged;
};

export class UserMediaMonitor {
  #abortController: AbortController;
  #config: UserMediaMonitorConfig;
  #deviceChangeHandler?: () => void;

  public media: MediaState;

  #stream: MediaStream | null = null;
  #availableDevices: MediaDeviceInfo[] = [];
  #customAudioTrack: MediaStreamTrack | null = null;
  #customAudioTrackSourceId: string | null = null;

  constructor(config?: UserMediaMonitorConfig) {
    this.#abortController = new AbortController();
    this.#config = config || {};

    this.media = {
      microphone: {
        health: { status: "idle", error: null, lastActive: null },
        activeDeviceId: null,
        stream: null,
        availableDevices: null,
        onError: undefined,
        fetchStatus: "idle",
        dataStatus: "idle",
      },
      video: {
        health: { status: "idle", error: null, lastActive: null },
        activeDeviceId: null,
        stream: null,
        availableDevices: null,
        onError: undefined,
        fetchStatus: "idle",
        dataStatus: "idle",
      },
      speaker: {
        health: { status: "idle", error: null, lastActive: null },
        activeDeviceId: null,
        stream: null,
        availableDevices: null,
        onError: undefined,
        fetchStatus: "idle",
        dataStatus: "idle",
      },
    };
  }

  get stream(): MediaStream | null {
    return this.#stream;
  }

  get availableDevices(): MediaDeviceInfo[] {
    return this.#availableDevices;
  }

  get customAudioTrack(): MediaStreamTrack | null {
    this.#syncCustomAudioTrack();
    return this.#customAudioTrack;
  }

  releaseCustomAudioTrack(): void {
    this.#clearCustomAudioTrack();
  }

  #sendUpdate(updates: Partial<MediaState>): void {
    const oldMedia = { ...this.media };
    const oldStream = this.#stream;

    // Apply partial updates to each device
    if (updates.microphone) {
      this.media.microphone = {
        ...this.media.microphone,
        ...updates.microphone,
      };
    }
    if (updates.video) {
      this.media.video = {
        ...this.media.video,
        ...updates.video,
      };
    }
    if (updates.speaker) {
      this.media.speaker = {
        ...this.media.speaker,
        ...updates.speaker,
      };
    }

    // Call the onMediaChange callback with old and new state (including stream)
    if (this.#config.onMediaChange) {
      this.#config.onMediaChange(
        { media: oldMedia, stream: oldStream },
        { media: this.media, stream: this.#stream },
      );
    }
  }

  #updateDeviceHealth(
    device: "microphone" | "video" | "speaker",
    health: Partial<DeviceHealth>,
    availableDevices?: MediaDeviceInfo[],
  ): void {
    this.#sendUpdate({
      [device]: {
        ...this.media[device],
        health: {
          ...this.media[device].health,
          ...health,
        },
        availableDevices: availableDevices ?? undefined,
      },
    });
  }

  async #fetchAvailableDevices(): Promise<void> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      this.#availableDevices = devices;

      // Update availableDevices for each device type
      const audioDevices = devices.filter((d) => d.kind === "audioinput");
      const videoDevices = devices.filter((d) => d.kind === "videoinput");
      const speakerDevices = devices.filter((d) => d.kind === "audiooutput");

      this.#sendUpdate({
        microphone: {
          ...this.media.microphone,
          availableDevices: audioDevices,
        },
        video: {
          ...this.media.video,
          availableDevices: videoDevices,
        },
        speaker: {
          ...this.media.speaker,
          availableDevices: speakerDevices,
        },
      });
    } catch (error) {
      console.error("Failed to enumerate devices:", error);
    }
  }

  #resolveDeviceId(
    kind: "audioinput" | "videoinput" | "audiooutput",
    deviceId: string | null,
  ): string | null {
    if (!deviceId) return null;
    if (deviceId !== "default" && deviceId !== "communications")
      return deviceId;
    const concrete = this.#availableDevices.find(
      (device) =>
        device.kind === kind &&
        device.deviceId !== "default" &&
        device.deviceId !== "communications" &&
        device.deviceId !== "",
    );
    return concrete?.deviceId ?? deviceId;
  }

  #setupTrackMonitoring(track: MediaStreamTrack): void {
    const deviceType = track.kind === "audio" ? "microphone" : "video";

    const handleTrackEnded = () => {
      console.warn(`${deviceType} track ended unexpectedly`);
      this.#updateDeviceHealth(deviceType, {
        status: "error",
        error: "Track ended unexpectedly",
      });
      if (this.media[deviceType].onError) {
        this.media[deviceType].onError!("Track ended unexpectedly");
      }
      if (this.#config.onDeviceError) {
        this.#config.onDeviceError(
          track.kind as DeviceKind,
          "Track ended unexpectedly",
        );
      }
    };
    const handleMute = () => {
      console.warn(`${deviceType} track muted`);
      this.#updateDeviceHealth(deviceType, {
        status: "warning",
        error: "Track is muted",
      });
      if (this.media[deviceType].onError) {
        this.media[deviceType].onError!("Track is muted");
      }
      if (this.#config.onDeviceError) {
        this.#config.onDeviceError(track.kind as DeviceKind, "Track is muted");
      }
    };
    const handleUnmute = () => {
      console.log(`${deviceType} track unmuted`);
      this.#updateDeviceHealth(deviceType, {
        status: "healthy",
        error: null,
        lastActive: Date.now(),
      });
    };

    track.addEventListener("ended", handleTrackEnded, {
      signal: this.#abortController.signal,
    });
    track.addEventListener("mute", handleMute, {
      signal: this.#abortController.signal,
    });
    track.addEventListener("unmute", handleUnmute, {
      signal: this.#abortController.signal,
    });

    // Set initial active state
    const rawDeviceId = track.getSettings?.().deviceId || null;
    const resolvedDeviceId = this.#resolveDeviceId(
      track.kind === "audio" ? "audioinput" : "videoinput",
      rawDeviceId,
    );
    const deviceId = resolvedDeviceId ?? rawDeviceId ?? track.id ?? null;
    this.#sendUpdate({
      [deviceType]: {
        ...this.media[deviceType],
        activeDeviceId: deviceId,
        stream: new MediaStream([track]),
        health: {
          status: "healthy",
          error: null,
          lastActive: Date.now(),
        },
        fetchStatus: "success",
        dataStatus: "success",
      },
    });
  }

  #clearCustomAudioTrack(): void {
    if (this.#customAudioTrack) {
      this.#customAudioTrack.stop();
    }
    this.#customAudioTrack = null;
    this.#customAudioTrackSourceId = null;
  }

  #syncCustomAudioTrack(): void {
    const sourceTrack = this.#stream?.getAudioTracks?.()[0] ?? null;
    const sourceId = sourceTrack?.id ?? null;

    if (!sourceTrack) {
      this.#clearCustomAudioTrack();
      return;
    }

    if (
      this.#customAudioTrack &&
      this.#customAudioTrackSourceId === sourceId &&
      this.#customAudioTrack.readyState !== "ended"
    ) {
      return;
    }

    this.#clearCustomAudioTrack();
    this.#customAudioTrack = sourceTrack.clone();
    this.#customAudioTrackSourceId = sourceId;
  }

  async request(
    type: "audio" | "video" | "audio-video" = "audio-video",
    constraints?: MediaStreamConstraints,
  ): Promise<MediaStream | null> {
    try {
      const mediaConstraints: MediaStreamConstraints = {
        ...(constraints ?? {}),
      };
      const shouldRequestAudio = type === "audio" || type === "audio-video";
      const shouldRequestVideo = type === "video" || type === "audio-video";
      const preferredAudioDeviceId = this.media.microphone.activeDeviceId;
      const preferredVideoDeviceId = this.media.video.activeDeviceId;
      const resolvedAudioDeviceId = this.#resolveDeviceId(
        "audioinput",
        preferredAudioDeviceId,
      );
      const resolvedVideoDeviceId = this.#resolveDeviceId(
        "videoinput",
        preferredVideoDeviceId,
      );

      if (
        resolvedAudioDeviceId &&
        resolvedAudioDeviceId !== preferredAudioDeviceId
      ) {
        this.#sendUpdate({
          microphone: {
            ...this.media.microphone,
            activeDeviceId: resolvedAudioDeviceId,
          },
        });
      }

      if (
        resolvedVideoDeviceId &&
        resolvedVideoDeviceId !== preferredVideoDeviceId
      ) {
        this.#sendUpdate({
          video: {
            ...this.media.video,
            activeDeviceId: resolvedVideoDeviceId,
          },
        });
      }

      if (shouldRequestAudio) {
        mediaConstraints.audio = mergeTrackConstraints(
          mediaConstraints.audio,
          {},
          resolvedAudioDeviceId,
        );
        this.#sendUpdate({
          microphone: {
            ...this.media.microphone,
            fetchStatus: "fetching",
            dataStatus:
              this.media.microphone.stream === null
                ? "loading"
                : this.media.microphone.dataStatus,
          },
        });
        this.#updateDeviceHealth("microphone", {
          status: "healthy",
          error: null,
        });
      }

      if (shouldRequestVideo) {
        mediaConstraints.video = mergeTrackConstraints(
          mediaConstraints.video,
          BALANCED_VIDEO_CONSTRAINTS,
          resolvedVideoDeviceId,
        );
        this.#sendUpdate({
          video: {
            ...this.media.video,
            fetchStatus: "fetching",
            dataStatus:
              this.media.video.stream === null
                ? "loading"
                : this.media.video.dataStatus,
          },
        });
        this.#updateDeviceHealth("video", { status: "healthy", error: null });
      }

      const stream =
        await navigator.mediaDevices.getUserMedia(mediaConstraints);
      this.#stream = stream;
      this.#syncCustomAudioTrack();

      // Fetch and update available devices after getting stream
      await this.#fetchAvailableDevices();

      // Setup monitoring for each track
      stream.getTracks().forEach((track) => {
        this.#setupTrackMonitoring(track);
      });

      return stream;
    } catch (error) {
      const fallbackMessage =
        error instanceof Error ? error.message : "Unknown error";
      const notFoundError =
        error instanceof DOMException &&
        (error.name === "NotFoundError" ||
          error.name === "DevicesNotFoundError");
      const resolveErrorMessage = (deviceType: "audio" | "video") => {
        if (!notFoundError) return fallbackMessage;
        return deviceType === "audio"
          ? "No microphone devices detected"
          : "No camera devices detected";
      };

      if (type === "audio" || type === "audio-video") {
        const audioErrorMessage = resolveErrorMessage("audio");
        this.#updateDeviceHealth("microphone", {
          status: "error",
          error: audioErrorMessage,
        });
        this.#sendUpdate({
          microphone: {
            ...this.media.microphone,
            fetchStatus: "error",
            dataStatus:
              this.media.microphone.stream === null ? "error" : "success",
          },
        });
        this.media.microphone.onError?.(audioErrorMessage);
        this.#config.onDeviceError?.("audio", audioErrorMessage);
      }

      if (type === "video" || type === "audio-video") {
        const videoErrorMessage = resolveErrorMessage("video");
        this.#updateDeviceHealth("video", {
          status: "error",
          error: videoErrorMessage,
        });
        this.#sendUpdate({
          video: {
            ...this.media.video,
            fetchStatus: "error",
            dataStatus: this.media.video.stream === null ? "error" : "success",
          },
        });
        this.media.video.onError?.(videoErrorMessage);
        this.#config.onDeviceError?.("video", videoErrorMessage);
      }

      return null;
    }
  }

  async onDeviceChange(): Promise<void> {
    await this.#fetchAvailableDevices();

    // Check if active devices are still available
    const audioDevices = this.#availableDevices.filter(
      (d) => d.kind === "audioinput",
    );
    const videoDevices = this.#availableDevices.filter(
      (d) => d.kind === "videoinput",
    );
    const speakerDevices = this.#availableDevices.filter(
      (d) => d.kind === "audiooutput",
    );

    // Handle microphone device changes
    if (this.media.microphone.activeDeviceId) {
      const stillExists = audioDevices.some(
        (d) => d.deviceId === this.media.microphone.activeDeviceId,
      );
      if (!stillExists) {
        this.#updateDeviceHealth(
          "microphone",
          {
            status: "error",
            error: "Microphone device disconnected",
          },
          audioDevices,
        );
        if (this.#config.onDeviceChange) {
          this.#config.onDeviceChange("audio", null);
        }
      }
    }

    // Handle video device changes
    if (this.media.video.activeDeviceId) {
      const stillExists = videoDevices.some(
        (d) => d.deviceId === this.media.video.activeDeviceId,
      );
      if (!stillExists) {
        this.#updateDeviceHealth(
          "video",
          {
            status: "error",
            error: "Video device disconnected",
          },
          videoDevices,
        );
        if (this.#config.onDeviceChange) {
          this.#config.onDeviceChange("video", null);
        }
      }
    }

    // Handle speaker device changes
    if (this.media.speaker.activeDeviceId) {
      const stillExists = speakerDevices.some(
        (d) => d.deviceId === this.media.speaker.activeDeviceId,
      );
      if (!stillExists) {
        this.#updateDeviceHealth(
          "speaker",
          {
            status: "error",
            error: "Speaker device disconnected",
          },
          speakerDevices,
        );
        if (this.#config.onDeviceChange) {
          this.#config.onDeviceChange("speaker", null);
        }
      }
    }
  }

  public async changeDeviceId(
    device: "microphone" | "video" | "speaker",
    deviceId: string | null,
  ): Promise<void> {
    // Stop existing tracks for this device type
    if (this.#stream) {
      const tracks =
        device === "microphone"
          ? this.#stream.getAudioTracks()
          : device === "video"
            ? this.#stream.getVideoTracks()
            : [];

      tracks.forEach((track) => track.stop());
    }

    const deviceKind: DeviceKind =
      device === "microphone"
        ? "audio"
        : device === "speaker"
          ? "speaker"
          : "video";

    const resolvedDeviceId = this.#resolveDeviceId(
      deviceKind === "audio"
        ? "audioinput"
        : deviceKind === "video"
          ? "videoinput"
          : "audiooutput",
      deviceId,
    );

    // Update the active device ID
    this.#sendUpdate({
      [device]: {
        ...this.media[device],
        activeDeviceId: resolvedDeviceId,
      },
    });

    // Call the onDeviceChange callback
    if (this.#config.onDeviceChange) {
      const deviceInfo =
        this.#availableDevices.find((d) => d.deviceId === resolvedDeviceId) ||
        null;
      this.#config.onDeviceChange(deviceKind, deviceInfo);
    }

    // For speaker, we don't need to request a new stream
    if (device === "speaker") {
      return;
    }

    // Request new stream with the selected device
    const constraints: MediaStreamConstraints = {};

    if (device === "microphone") {
      constraints.audio = resolvedDeviceId
        ? { deviceId: { exact: resolvedDeviceId } }
        : true;
    } else if (device === "video") {
      constraints.video = resolvedDeviceId
        ? { deviceId: { exact: resolvedDeviceId } }
        : true;
    }
    await this.request("audio-video", constraints);
  }

  public async activate(device: "microphone" | "video"): Promise<void> {
    const constraints: MediaStreamConstraints = {};

    if (device === "microphone") {
      const resolvedDeviceId = this.#resolveDeviceId(
        "audioinput",
        this.media.microphone.activeDeviceId,
      );
      constraints.audio = resolvedDeviceId
        ? { deviceId: { exact: resolvedDeviceId } }
        : true;
      await this.request("audio", constraints);
    } else {
      const resolvedDeviceId = this.#resolveDeviceId(
        "videoinput",
        this.media.video.activeDeviceId,
      );
      constraints.video = resolvedDeviceId
        ? { deviceId: { exact: resolvedDeviceId } }
        : true;
      await this.request("video", constraints);
    }
  }

  public deactivate(device: "microphone" | "video"): void {
    if (this.#stream) {
      const tracks =
        device === "microphone"
          ? this.#stream.getAudioTracks()
          : this.#stream.getVideoTracks();

      tracks.forEach((track) => track.stop());

      if (device === "microphone") {
        this.#clearCustomAudioTrack();
      }

      this.#sendUpdate({
        [device]: {
          ...this.media[device],
          stream: null,
          health: {
            status: "idle",
            error: null,
            lastActive: Date.now(),
          },
          fetchStatus: "idle",
          dataStatus: "idle",
        },
      });
    }
  }

  public startDeviceChangeMonitoring(): void {
    this.#deviceChangeHandler = () => this.onDeviceChange();
    navigator.mediaDevices.addEventListener(
      "devicechange",
      this.#deviceChangeHandler,
      { signal: this.#abortController.signal },
    );
  }

  public destroy(): void {
    this.#abortController.abort();
    this.#clearCustomAudioTrack();
    if (this.#stream) {
      this.#stream.getTracks().forEach((track) => track.stop());
    }
    this.#stream = null;
  }
}

export const createUserMediaMonitor = (
  config?: UserMediaMonitorConfig,
): UserMediaMonitor => {
  const monitor = new UserMediaMonitor(config);
  monitor.startDeviceChangeMonitoring();
  return monitor;
};

interface MediaState {
  microphone: MediaDeviceConfig;
  video: MediaDeviceConfig;
  speaker: MediaDeviceConfig;
}

interface UserMediaMonitorConfig {
  onMediaChange?: (
    oldValue: { media: MediaState; stream: MediaStream | null },
    newValue: { media: MediaState; stream: MediaStream | null },
  ) => void;
  onDeviceChange?: (kind: DeviceKind, device: MediaDeviceInfo | null) => void;
  onDeviceError?: (kind: DeviceKind, error: string) => void;
}
